<template>
    <div class="card">
        <div class="card-header bg-white">
            <div class="d-flex justify-content-between align-items-center">
                <h4 class="card-title mb-0">{{ $t('label.listingX', [$t('module.booking')]) }}</h4>
                <div class="d-flex align-items-center">
                    <b-button
                        variant="secondary"
                        class="mr-1"
                        :disabled="!status.list || loading"
                        :title="$t('label.refresh')"
                        v-b-tooltip.hover
                        @click="refreshListing"
                    >
                        <i class="mdi mdi-refresh"></i>
                    </b-button>
                    <b-dropdown variant="warning" no-caret menu-class="dropdown-menu-250" class="mr-1" right>
                        <template #button-content>
                            <i class="mdi mdi-filter"></i>
                        </template>
                        <div class="px-3 pb-2">
                            <label class="small text-muted mb-1">{{ $t('label.startDate') }}</label>
                            <DatePicker
                                v-model="dateFrom"
                                :config="{ format: 'YYYY-MM-DD', useCurrent: false }"
                                class="w-100 cursor-pointer"
                                autocomplete="off"
                                @dp-change="searchListing"
                            />
                        </div>
                        <div class="px-3 pb-2">
                            <label class="small text-muted mb-1">{{ $t('label.endDate') }}</label>
                            <DatePicker
                                v-model="dateTo"
                                :config="{ format: 'YYYY-MM-DD', useCurrent: false }"
                                class="w-100 cursor-pointer"
                                autocomplete="off"
                                @dp-change="searchListing"
                            />
                        </div>
                    </b-dropdown>
                    <b-button
                        variant="info"
                        class="mr-1"
                        :title="$t('label.createX', [])"
                        @click="openForm"
                        v-b-tooltip.hover
                    >
                        <i class="mdi mdi-plus"></i>
                    </b-button>
                </div>
            </div>
        </div>
        <div class="card-body px-3 pt-0 pb-0">
            <div v-if="status.list && !loading" class="row">
                <div v-for="item in tableRows" :key="item.uuid" class="col-xl-3 col-lg-4 col-md-6 col-12 mb-3">
                    <div class="card border border-light shadow-lg h-100 mb-0">
                        <div class="card-header bg-info text-white d-flex justify-content-between align-items-center">
                            <div class="font-weight-bold text-truncate" :title="item.agenda">{{ item.agenda || '--' }}</div>
                            <b-button
                                type="button"
                                variant="link"
                                class="text-white p-0 ml-2 flex-shrink-0"
                                :disabled="!status.save"
                                :title="$t('label.deleteX', [])"
                                v-b-tooltip.hover
                                @click="deleteConfirmation(item)"
                            >
                                <i class="mdi mdi-delete-outline font-size-18"></i>
                            </b-button>
                        </div>
                        <div class="card-body">
                            <div class="mb-2">
                                <small class="text-muted d-block">Meeting Room</small>
                                <strong>{{ item.meetingRoom?.name || '--' }}</strong>
                            </div>
                            <div class="mb-2">
                                <small class="text-muted d-block">Date</small>
                                <strong>{{ item.date || '--' }}</strong>
                            </div>
                            <div>
                                <small class="text-muted d-block">Time</small>
                                <strong>{{ item.start || '--' }} - {{ item.end || '--' }}</strong>
                            </div>
                            <div v-if="item.description" class="mt-3">
                                <b-button
                                    :id="'booking-description-' + item.uuid"
                                    type="button"
                                    variant="link"
                                    size="sm"
                                    class="text-muted p-0 d-inline-flex align-items-center text-decoration-none"
                                >
                                    <i class="mdi mdi-text-box-outline mr-1 font-size-16 text-info"></i>
                                    <span class="font-size-12">
                                        {{ $t('label.description') }}
                                    </span>
                                    <i class="mdi mdi-chevron-down ml-1 font-size-14"></i>
                                </b-button>
                                <b-popover
                                    :target="'booking-description-' + item.uuid"
                                    triggers="hover focus"
                                    placement="bottom"
                                    custom-class="border border-light shadow-lg"
                                >
                                    <div class="text-break" style="white-space: pre-line;">{{ item.description }}</div>
                                </b-popover>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-if="tableRows.length == 0" class="col-12">
                    <b-alert show variant="warning" class="text-center">
                        <i class="mdi mdi-alert-circle-outline"></i> {{ $t('message.noResultsFound') }}
                    </b-alert>
                </div>
            </div>
            <div v-else class="d-flex justify-content-center align-items-center p-5 m-5">
                <b-spinner class="m-3" variant="info" role="status" />
            </div>
            <div v-if="totalPage && status.list && !loading" class="d-flex flex-column flex-md-row justify-content-md-between align-items-center">
                <small class="text-muted">{{ $t('label.total') }}: {{ totalPage }}</small>
                <b-pagination
                    v-model="currentPage"
                    :total-rows="totalPage"
                    :per-page="perPage"
                    v-on:change="pageNavigation"
                    variant="info"
                />
            </div>
        </div>
        <b-modal
            id="meeting-room-booking-create"
            :title="$t('label.createX', [$t('module.booking')])"
            @hidden="closeForm"
            hide-footer
            hide-header-close
            centered
            size="xl"
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
        >
            <MeetingRoomBookingForm
                v-if="showForm"
                @done="closeForm"
                @saved="refreshListing"
            />
        </b-modal>
        <b-modal
            id="meeting-room-booking-delete"
            :title="$t('label.deleteX', [$t('module.booking')])"
            @hidden="closeDelete"
            hide-header-close
            :no-close-on-backdrop="!status.save"
            :no-close-on-esc="!status.save"
        >
            <p class="my-4">{{ $t('message.deleteConfirmation') }}</p>
            <template #modal-footer>
                <b-button variant="danger" class="col-3" :disabled="!status.save" @click="$bvModal.hide('meeting-room-booking-delete')">
                    {{ $t('label.cancel') }}
                </b-button>
                <b-button variant="info" class="ml-2 col-3" :disabled="!status.save" v-on:click="requestDelete">
                    <span v-if="status.save">{{ $t('label.deleteX', []) }}</span>
                    <b-spinner v-else small />
                </b-button>
            </template>
        </b-modal>
    </div>
</template>
<script>
import MeetingRoomBookingForm from "@/views/pages/ihextar/meetingRoomBooking/meetingRoomBookingForm";
import moment from 'moment';
import DatePicker from 'vue-bootstrap-datetimepicker';
import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css';
import { SESSION, SHARED } from "@/constants";
import { sessionHelper } from "@/utils";
import { mapState } from "vuex";
import { meetingRoomBookingMethods } from "@/state/app";
import { MEETING_ROOM_BOOKING_MODULE } from "@/state/app/meetingRoomBooking.state";

export default {
    components: { DatePicker, MeetingRoomBookingForm },
    async created() {
        sessionHelper.clearSearch(SESSION.APP_MEETING_ROOM_BOOKING_SEARCH);
        await this.initSearchCriteria();
        this.loading = false;
    },
    data() {
        return {
            loading: true,
            showForm: false,
            booking: {
                uuid: "",
            },
            currentPage: 1,
            perPage: SHARED.DEFAULT_PAGE_SIZE,
            dateFrom: moment().format('YYYY-MM-DD'),
            dateTo: moment().add(1, 'day').format('YYYY-MM-DD'),
            searchCriteria: {},
        };
    },
    methods: {
        ...meetingRoomBookingMethods,
        openForm() {
            this.showForm = true;
            this.$nextTick(() => this.$bvModal.show('meeting-room-booking-create'));
        },
        closeForm() {
            this.showForm = false;
            this.$bvModal.hide('meeting-room-booking-create');
        },
        initSearchCriteria() {
            this.searchCriteria = {
                pageNumber: SHARED.DEFAULT_PAGE_NUMBER,
                pageSize: SHARED.DEFAULT_PAGE_SIZE,
                meetingDateStart: this.dateFrom,
                meetingDateEnd: this.dateTo,
                isShowAll: 0,
                isActive: 1,
            };
            return this.getMeetingRoomBookingList(this.searchCriteria);
        },
        getMeetingRoomBookingList(filter) {
            filter.isShowAll = 0;
            filter.isActive = 1;
            this.searchCriteria = filter;
            sessionHelper.storeSearch(SESSION.APP_MEETING_ROOM_BOOKING_SEARCH, filter);
            return this.getAllMeetingRoomBooking({filter});
        },
        deleteConfirmation(item) {
            this.booking = {
                uuid: item.uuid,
            };
            this.$bvModal.show('meeting-room-booking-delete');
        },
        requestDelete() {
            let data = {
                uuid: this.booking.uuid,
            };
            this.deleteMeetingRoomBooking({data, filter: this.searchCriteria})
                .then((result) => this.$nextTick(() => {
                    if (result?.success) this.$bvModal.hide('meeting-room-booking-delete');
                }));
        },
        closeDelete() {
            this.booking = {
                uuid: "",
            };
        },
        refreshListing() {
            this.getMeetingRoomBookingList(this.searchCriteria);
        },
        searchListing() {
            if (this.loading) return;
            this.currentPage = 1;
            this.searchCriteria.pageNumber = SHARED.DEFAULT_PAGE_NUMBER;
            this.searchCriteria.meetingDateStart = this.dateFrom;
            this.searchCriteria.meetingDateEnd = this.dateTo;
            this.getMeetingRoomBookingList(this.searchCriteria);
        },
        pageNavigation(pageNumber) {
            this.currentPage = pageNumber;
            let filter = sessionHelper.retrieveSearch(SESSION.APP_MEETING_ROOM_BOOKING_SEARCH);
            filter.pageNumber = pageNumber;
            this.getMeetingRoomBookingList(filter);
        },
    },
    computed: {
        ...mapState(MEETING_ROOM_BOOKING_MODULE, ['status']),
        tableRows() {
            return this.$store.state.app.meetingRoomBooking.list.data || [];
        },
        totalPage() {
            return this.$store.state.app.meetingRoomBooking.list.pagination.total || 0;
        },
    },
}
</script>
